import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';

export default function NotFound() {
  const location = useLocation();
  const [suggestions] = useState([
    {
      title: 'View Our Portfolio',
      path: '/gallery',
      description: 'Explore our stunning kitchen and bathroom remodels'
    },
    {
      title: 'Our Process',
      path: '/process',
      description: 'Learn how we bring your vision to life'
    },
    {
      title: 'Contact Us',
      path: '/contact',
      description: 'Get in touch for a free consultation'
    }
  ]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="flex flex-col justify-center items-center px-4 py-16 min-h-screen bg-gradient-to-b from-white to-gray-50">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="mx-auto w-full max-w-4xl text-center">
        {/* 404 Header Section */}
        <motion.div variants={itemVariants} className="mb-8">
          <h1 className="mb-4 text-8xl font-bold text-blue-600">404</h1>
          <div className="mx-auto mb-6 w-24 h-1 bg-primary"></div>
          <h2 className="mb-4 text-3xl font-semibold text-gray-800">
            Page Not Found
          </h2>
          <p className="mx-auto max-w-2xl text-lg leading-relaxed text-gray-600">
            We couldn't find the page you're looking for at{' '}
            <span className="px-2 py-1 font-mono bg-gray-100 rounded">
              {location.pathname}
            </span>
          </p>
        </motion.div>

        {/* Primary CTA */}
        <motion.div variants={itemVariants} className="mb-12">
          <Link
            to="/"
            className="inline-flex items-center px-8 py-4 font-semibold text-white bg-primary rounded-lg shadow-lg transition-all duration-200 transform hover:bg-primary-hover hover:scale-105 hover:shadow-xl">
            <svg
              className="mr-2 w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              />
            </svg>
            Return Home
          </Link>
        </motion.div>

        {/* Helpful Suggestions */}
        <motion.div variants={itemVariants}>
          <h3 className="mb-6 text-xl font-semibold text-gray-700">
            You might be interested in:
          </h3>
          <div className="grid gap-6 md:grid-cols-3">
            {suggestions.map((suggestion, index) => (
              <motion.div
                key={suggestion.path}
                variants={itemVariants}
                whileHover={{ scale: 1.03 }}
                className="p-6 bg-white rounded-lg shadow-md transition-all duration-200 hover:shadow-lg">
                <Link to={suggestion.path} className="block h-full">
                  <h4 className="mb-2 text-lg font-semibold text-blue-600">
                    {suggestion.title}
                  </h4>
                  <p className="text-sm text-gray-600">
                    {suggestion.description}
                  </p>
                </Link>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Help Text */}
        <motion.p
          variants={itemVariants}
          className="mt-12 text-sm text-gray-500">
          If you believe this is an error, please{' '}
          <Link
            to="/contact"
            className="text-blue-600 underline hover:text-blue-800">
            contact our support team
          </Link>
          .
        </motion.p>
      </motion.div>
    </div>
  );
}
